import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to prevent page flips after zoom gestures in flipbook components
 * Uses event capture phase to intercept touch events before they reach the flipbook library
 */
export const useZoomProtection = () => {
  const [isZooming, setIsZooming] = useState(false);
  const [justZoomed, setJustZoomed] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const zoomTimeoutRef = useRef(null);
  const initialScaleRef = useRef(1);

  useEffect(() => {
    // Initialize scale from Visual Viewport API if available
    if (window.visualViewport) {
      initialScaleRef.current = window.visualViewport.scale;
      setZoomScale(window.visualViewport.scale);
    }

    const handleTouchStart = (e) => {
      // Detect if this is a potential zoom gesture (2+ fingers)
      if (e.touches.length >= 2) {
        setIsZooming(true);
        // Clear any existing timeout
        if (zoomTimeoutRef.current) {
          clearTimeout(zoomTimeoutRef.current);
        }
      }
    };

    const handleTouchMove = (e) => {
      // If zooming with multiple fingers, prevent page flip
      if (isZooming && e.touches.length >= 2) {
        e.stopPropagation(); // Prevent flipbook from seeing this
      }
    };

    const handleTouchEnd = (e) => {
      if (isZooming) {
        // Mark that we just finished zooming
        setJustZoomed(true);
        setIsZooming(false);
        
        // Prevent page flip for a short period after zoom ends
        zoomTimeoutRef.current = setTimeout(() => {
          setJustZoomed(false);
        }, 300); // 300ms cooldown after zoom
        
        // Prevent the touchend from reaching flipbook
        e.stopPropagation();
        e.preventDefault();
      }
    };

    // Visual Viewport API for more precise zoom detection
    const handleViewportChange = () => {
      if (!window.visualViewport) return;
      
      const currentScale = window.visualViewport.scale;
      const scaleDifference = Math.abs(currentScale - initialScaleRef.current);
      
      // If scale changed significantly, we were zooming
      if (scaleDifference > 0.05) {
        setJustZoomed(true);
        // Reset the flag after a delay
        setTimeout(() => setJustZoomed(false), 500);
      }
      
      setZoomScale(currentScale);
    };

    // Add event listeners with capture phase to intercept before flipbook
    document.addEventListener('touchstart', handleTouchStart, { capture: true });
    document.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    document.addEventListener('touchend', handleTouchEnd, { capture: true, passive: false });

    // Add Visual Viewport API listener if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    }

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
      
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, [isZooming]);

  return { 
    isZooming, 
    justZoomed, 
    zoomScale,
    isZoomed: Math.abs(zoomScale - initialScaleRef.current) > 0.05
  };
};
